import { atom, useAtomValue } from 'jotai'
import { useSidepanelFloatingBarHeight, useSidepanelHeaderHeight } from '../../atoms'
import { SIDEPANEL_GUTTER_PX, SIDEPANEL_SURFACE_BORDER_PX } from '../../constants'
import { useSidepanelVerticalLayout } from '../../hooks/use-sidepanel-vertical-layout'
import type { MeetingNotepadSize } from './atoms'

/** Editor height the notepad opens at, per the Figma footer-swap anatomy. */
export const NOTEPAD_EXPANDED_PX = 200
/**
 * Collapsed to its header row alone: 20px of row under the 20px of top padding
 * the grab handle sits in, over 12px at the bottom, plus 2px the header doesn't
 * take. The header is top-anchored, so that slack all lands under it — optical
 * correction for the panel's drop shadow, which reads as the bottom edge
 * crowding the row when the two gaps measure the same.
 */
export const NOTEPAD_COLLAPSED_PX = 54
/** The inset the panel floats at, matching the chat input it stands in for. */
export const NOTEPAD_INSET_PX = 12
/**
 * Extra room above the floating bar at full height, on top of that gutter.
 *
 * What tops out there is a row of shadowed pills, not a card edge, and the
 * gutter's 12px under the header's hard rule reads tighter than the same 12px
 * beside the panel's edges — the pills' own drop shadow eats the difference.
 * Landed by eye against the sides — 16 read loose, 8 still read short. The same
 * illusion {@link NOTEPAD_COLLAPSED_PX} corrects for at the other end of the
 * panel, so the correction is optical rather than derived.
 */
const FLOATING_BAR_TOP_OPTICAL_PX = 13

/**
 * The height an expanded notepad returns to — its opening height until the user
 * drags its top edge, and whatever they dragged it to afterwards.
 *
 * Outside {@link meetingNotepadModeAtom} on purpose: the mode is rebuilt from
 * scratch on every open so a reopen can't inherit a stale disclosure state, and
 * a height the user set by hand is the one thing that *should* survive that.
 * In memory rather than on disk, so it holds for the session and opens at the
 * Figma height on a fresh run.
 */
export const meetingNotepadRestingHeightAtom = atom<number>(NOTEPAD_EXPANDED_PX)

/**
 * Set while the notepad's top edge is being dragged, which is the one time its
 * height is driven frame by frame rather than moved between two resting values.
 * The footer slot drops its morph while it's true — a 250ms ease under a live
 * drag reads as the edge lagging the pointer.
 */
export const meetingNotepadDraggingAtom = atom(false)

/**
 * The tallest the notepad can be: everything under the panel header, less the
 * room the floating bar needs. Derived from the panel's resolved height rather
 * than measured, so it lands on the first frame of the morph instead of a frame
 * into it, and so a drag can clamp against it without waiting for a layout.
 *
 * The bar rides above the footer's box rather than in it, so the notepad has to
 * leave it that room or it would be pushed past the panel's top edge — and the
 * panel's root doesn't clip, so it would hang over the desktop rather than
 * disappear. Leaving room is also what keeps the recording controls to hand at
 * full height: they sit at the top, directly above the notes.
 *
 * No circularity in reading the bar's own height back: what the bar publishes
 * turns on which view is open, never on how tall the notepad is.
 */
function useFullNotepadHeight(): number {
  const { height: panelHeight } = useSidepanelVerticalLayout()
  const headerHeight = useSidepanelHeaderHeight()
  const floatingBarHeight = useSidepanelFloatingBarHeight()
  const viewTop = SIDEPANEL_GUTTER_PX + SIDEPANEL_SURFACE_BORDER_PX + headerHeight
  const notepadBottom = SIDEPANEL_GUTTER_PX + NOTEPAD_INSET_PX
  // Only when the bar is actually standing there — with nothing published, the
  // notepad tops out at the gutter like any other surface and owes no optics.
  const barRoom = floatingBarHeight > 0 ? floatingBarHeight + FLOATING_BAR_TOP_OPTICAL_PX : 0

  // Never shorter than resting: on a panel dragged down to its minimum the
  // subtraction can go past 200px, and "full height" that shrinks the notepad
  // is worse than one that simply has nowhere to grow.
  return Math.max(NOTEPAD_EXPANDED_PX, panelHeight - viewTop - notepadBottom - barRoom)
}

/**
 * What each size renders at — the one place a {@link MeetingNotepadSize} turns
 * into pixels. The claim sizes the footer's box from it and the drag reads its
 * starting point from it; two spellings of the same table is a notepad that
 * jumps under the pointer the moment they drift.
 *
 * `normal` is clamped to what the panel can hold: a height dragged out on a
 * tall panel must not survive as an overflow on a short one, or on the next
 * display the window opens on.
 */
export function useMeetingNotepadHeights(): Record<MeetingNotepadSize, number> {
  const resting = useAtomValue(meetingNotepadRestingHeightAtom)
  const full = useFullNotepadHeight()

  return { collapsed: NOTEPAD_COLLAPSED_PX, normal: Math.min(resting, full), full }
}
